import { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { notificationService } from '../utils/notificationService';
import type { 
  Notification, 
  NotificationStats, 
  NotificationPreferences
} from '../utils/notificationService';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  stats: NotificationStats | null;
  preferences: NotificationPreferences | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
  } | null;
  unauthorized: boolean;
  authChecked: boolean;
}

type NotificationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_NOTIFICATIONS'; payload: { notifications: Notification[]; pagination: any } }
  | { type: 'SET_UNREAD_COUNT'; payload: number }
  | { type: 'SET_STATS'; payload: NotificationStats }
  | { type: 'SET_PREFERENCES'; payload: NotificationPreferences }
  | { type: 'MARK_AS_READ'; payload: number[] }
  | { type: 'MARK_ALL_AS_READ' }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: number }
  | { type: 'UPDATE_NOTIFICATION'; payload: Notification }
  | { type: 'SET_UNAUTHORIZED'; payload: boolean }
  | { type: 'SET_AUTH_CHECKED'; payload: boolean };

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  stats: null,
  preferences: null,
  loading: false,
  error: null,
  pagination: null,
  unauthorized: false,
  authChecked: false,
};

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_NOTIFICATIONS':
      return { 
        ...state, 
        notifications: action.payload.notifications,
        pagination: action.payload.pagination
      };
    
    case 'SET_UNREAD_COUNT':
      return { ...state, unreadCount: action.payload };
    
    case 'SET_STATS':
      return { ...state, stats: action.payload };
    
    case 'SET_PREFERENCES':
      return { ...state, preferences: action.payload };

    case 'SET_UNAUTHORIZED':
      return { ...state, unauthorized: action.payload };
    
    case 'SET_AUTH_CHECKED':
      return { ...state, authChecked: action.payload };
    
    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          action.payload.includes(notification.id)
            ? { ...notification, is_read: true }
            : notification
        ),
        unreadCount: Math.max(
          0,
          state.unreadCount -
            state.notifications.filter(
              notification =>
                !notification.is_read && action.payload.includes(notification.id)
            ).length
        )
      };

    case 'MARK_ALL_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification => ({
          ...notification,
          is_read: true
        })),
        unreadCount: 0
      };
    
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1
      };
    
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
        unreadCount: Math.max(
          0,
          state.unreadCount -
            (state.notifications.some(
              notification =>
                notification.id === action.payload && !notification.is_read
            )
              ? 1
              : 0)
        )
      };
    
    case 'UPDATE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload.id ? action.payload : n
        )
      };
    
    default:
      return state;
  }
}

interface NotificationContextType {
  state: NotificationState;
  fetchNotifications: (filters?: any) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchPreferences: () => Promise<void>;
  markAsRead: (notificationIds: number[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  updatePreferences: (preferences: Partial<NotificationPreferences>) => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  addNotification: (notification: Notification) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const latestNotificationsRequest = useRef(0);

  const runFetch = async (fetchFn: () => Promise<void>) => {
    await fetchFn();
  };

  const fetchNotifications = async (filters: any = {}) => {
    const requestId = ++latestNotificationsRequest.current;
    await runFetch(async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        const response = await notificationService.getNotifications(filters);

        if (requestId !== latestNotificationsRequest.current) {
          return;
        }

        if (response && response.success) {
          dispatch({
            type: 'SET_NOTIFICATIONS',
            payload: {
              notifications: response.notifications,
              pagination: response.pagination
            }
          });
          dispatch({ type: 'SET_UNAUTHORIZED', payload: false });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
        } else if (response && response.success === false && response.error === 'Unauthorized') {
          // Backend returned explicit unauthorized
          dispatch({ type: 'SET_UNAUTHORIZED', payload: true });
          dispatch({ type: 'SET_NOTIFICATIONS', payload: { notifications: [], pagination: null } });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
        } else {
          dispatch({ type: 'SET_ERROR', payload: response?.error || 'Failed to fetch notifications' });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
        }
      } catch (error) {
        if (requestId !== latestNotificationsRequest.current) {
          return;
        }

        // If axios returns 401, mark unauthorized so UI can hide the bell
        const status = (error as any)?.response?.status;
        if (status === 401) {
          dispatch({ type: 'SET_UNAUTHORIZED', payload: true });
          dispatch({ type: 'SET_NOTIFICATIONS', payload: { notifications: [], pagination: null } });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
        } else {
          dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch notifications' });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
        }
      } finally {
        if (requestId === latestNotificationsRequest.current) {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    });
  };

  const fetchUnreadCount = async () => {
    await runFetch(async () => {
      try {
        const response = await notificationService.getUnreadCount();
        if (response.success) {
          dispatch({ type: 'SET_UNREAD_COUNT', payload: response.count });
        }
      } catch (error) {
        const status = (error as any)?.response?.status;
        if (status === 401) {
          dispatch({ type: 'SET_UNAUTHORIZED', payload: true });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
          return;
        }
        console.error('Failed to fetch unread count:', error);
      }
    });
  };

  const fetchStats = async () => {
    await runFetch(async () => {
      try {
        const response = await notificationService.getNotificationStats();
        if (response.success) {
          dispatch({ type: 'SET_STATS', payload: response.stats });
        }
      } catch (error) {
        const status = (error as any)?.response?.status;
        if (status === 401) {
          dispatch({ type: 'SET_UNAUTHORIZED', payload: true });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
          return;
        }
        console.error('Failed to fetch notification stats:', error);
      }
    });
  };

  const fetchPreferences = async () => {
    await runFetch(async () => {
      try {
        const response = await notificationService.getPreferences();
        if (response.success) {
          dispatch({ type: 'SET_PREFERENCES', payload: response.preferences });
        }
      } catch (error) {
        const status = (error as any)?.response?.status;
        if (status === 401) {
          dispatch({ type: 'SET_UNAUTHORIZED', payload: true });
          dispatch({ type: 'SET_AUTH_CHECKED', payload: true });
          return;
        }
        console.error('Failed to fetch preferences:', error);
      }
    });
  };

  const markAsRead = async (notificationIds: number[]) => {
    try {
      const response = await notificationService.markAsRead(notificationIds);
      if (response.success) {
        dispatch({ type: 'MARK_AS_READ', payload: notificationIds });
      }
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await notificationService.markAllAsRead();
      if (response.success) {
        dispatch({ type: 'MARK_ALL_AS_READ' });
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const updatePreferences = async (preferences: Partial<NotificationPreferences>) => {
    try {
      const response = await notificationService.updatePreferences(preferences);
      if (response.success) {
        await fetchPreferences();
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
    }
  };

  const deleteNotification = async (notificationId: number) => {
    try {
      const response = await notificationService.deleteNotification(notificationId);
      if (response.success) {
        dispatch({ type: 'REMOVE_NOTIFICATION', payload: notificationId });
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const addNotification = (notification: Notification) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
  };

  // Initial data fetch
  useEffect(() => {
    fetchNotifications({ per_page: 5, status: 'unread' });
    fetchUnreadCount();
    fetchStats();
    fetchPreferences();
  }, []);

  const value: NotificationContextType = {
    state,
    fetchNotifications,
    fetchUnreadCount,
    fetchStats,
    fetchPreferences,
    markAsRead,
    markAllAsRead,
    updatePreferences,
    deleteNotification,
    addNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
} 